/**
 * SystemsTab - Vessel systems configuration tab
 */

import React from 'react';
import { parseCategory } from '../../../../utils/categoryHelpers';
import { Select } from '../../../catalyst/select';
import { Switch } from '../../../catalyst/checkbox';
import NavigationEquipment from './components/NavigationEquipment';
import SafetyEquipment from './components/SafetyEquipment';
import VesselSystems from './components/VesselSystems';

const SystemsTab = ({ vesselConfig, updateConfig }) => {
  // Determine which equipment is mandatory based on vessel configuration
  const getMandatoryEquipment = () => {
    const mandatory = [];
    const category = parseCategory(vesselConfig.category);
    
    // Use auto-required equipment from BasicInfoTab if available
    if (vesselConfig.autoRequiredEquipment) {
      mandatory.push(...vesselConfig.autoRequiredEquipment);
    } else {
      // Fallback to manual calculation
      // GPS - Mandatory for Area 0-4
      if (category <= 4) {
        mandatory.push('gps');
      }
      
      // Echo Sounder - Mandatory for Area 0-4  
      if (category <= 4) {
        mandatory.push('echo_sounder');
      }
      
      // Radar - Mandatory for Area 0-2
      if (category <= 2) {
        mandatory.push('radar');
      }
      
      // AIS - Mandatory if ≥300 GT or carries passengers
      if (vesselConfig.grossTonnage >= 300 || vesselConfig.passengerCapacity > 0) {
        mandatory.push('ais');
      }
      
      // NAVTEX - Mandatory for Area 0-2
      if (category <= 2) {
        mandatory.push('navtex');
      }
      
      // MF Radio - Mandatory for Area 0-1
      if (category <= 1) {
        mandatory.push('mf_radio');
      }
    }
    
    // Add mandatory items from PA system and General Alarm
    if (vesselConfig.autoRequiredPASystem && !mandatory.includes('pa_system')) {
      mandatory.push('pa_system');
    }
    
    if (vesselConfig.autoRequiredGeneralAlarm && !mandatory.includes('general_alarm')) {
      mandatory.push('general_alarm');
    }
    
    // Add fixed fire system if required
    if (vesselConfig.autoRequiredFireSystem && !mandatory.includes('fixed_fire_system')) {
      mandatory.push('fixed_fire_system');
    }
    
    // Add accommodation systems if required
    if (vesselConfig.autoRequiredAccommodation) {
      ['hvac', 'galley_equipment', 'watermaker'].forEach(eq => {
        if (!mandatory.includes(eq)) {
          mandatory.push(eq);
        }
      });
    }
    
    return mandatory;
  };

  // Get vessel type-specific mandatory equipment
  const getVesselTypeEquipment = () => {
    const vesselTypeEquipment = [];
    
    // Add vessel type mandatory equipment if available
    if (vesselConfig.vesselTypeMandatoryEquipment) {
      vesselTypeEquipment.push(...vesselConfig.vesselTypeMandatoryEquipment);
    } else {
      // Fallback to hardcoded requirements per vessel type
      switch (vesselConfig.vesselType) {
        case 'tug':
          vesselTypeEquipment.push('towing_lights', 'heavy_duty_fenders', 'towing_winch');
          break;
        case 'pilot':
          vesselTypeEquipment.push('searchlight', 'immersion_suits', 'line_throwing_apparatus');
          break;
        case 'police':
          vesselTypeEquipment.push('immersion_suits', 'enhanced_communications', 'blue_lights');
          break;
        case 'pax':
          vesselTypeEquipment.push('pa_system', 'general_alarm', 'additional_liferafts');
          break;
        case 'ctv':
          vesselTypeEquipment.push('pa_system', 'motion_compensated_gangway');
          break;
      }
    }
    
    return vesselTypeEquipment;
  };

  const mandatoryEquipment = getMandatoryEquipment();
  const vesselTypeEquipment = getVesselTypeEquipment();

  // Helper to get vessel type display name
  const getVesselTypeDisplayName = (type) => {
    const names = {
      tug: 'TUG',
      pax: 'PASSENGER',
      ctv: 'CTV',
      pilot: 'PILOT BOAT',
      police: 'POLICE',
      rib: 'RIB',
      survey: 'SURVEY',
      support: 'SUPPORT',
      patrol: 'PATROL',
      cargo: 'CARGO',
      utility: 'UTILITY',
      multipurpose: 'MULTIPURPOSE',
      fishing_support: 'FISHING SUPPORT',
      hydrographic: 'HYDROGRAPHIC',
      dredger: 'DREDGER',
      safety_standby: 'SAFETY/STANDBY',
      dive_support: 'DIVE SUPPORT'
    };
    return names[type] || type?.toUpperCase();
  };

  // Helper to get equipment display names
  const getEquipmentDisplayName = (equipment) => {
    const names = {
      // Tug equipment
      'towing_lights': 'Towing Lights',
      'heavy_duty_fenders': 'Heavy Duty Fenders',
      'towing_winch': 'Towing Winch',
      // Pilot boat equipment
      'searchlight': 'Searchlight',
      'immersion_suits': 'Immersion Suits (All Persons)',
      'line_throwing_apparatus': 'Line Throwing Apparatus',
      // Police equipment
      'enhanced_communications': 'Enhanced Communications',
      'blue_lights': 'Blue Emergency Lights',
      // Passenger equipment
      'pa_system': 'Public Address System',
      'general_alarm': 'General Alarm System',
      'additional_liferafts': 'Additional Liferafts',
      // CTV equipment
      'motion_compensated_gangway': 'Motion Compensated Gangway',
      // Safety/Standby equipment
      'enhanced_first_aid': 'Enhanced First Aid Equipment',
      'rescue_boat': 'Fast Rescue Boat',
      // Dive support equipment
      'dive_platform': 'Dive Platform',
      'emergency_dive_equipment': 'Emergency Dive Equipment',
      // Dredger equipment
      'stability_monitoring': 'Stability Monitoring System',
      // Additional equipment that may be manually selected
      'bow_thruster': 'Bow Thruster',
      'windlass': 'Anchor Windlass',
      'capstan': 'Capstan',
      'mooring_winches': 'Mooring Winches',
      'generator': 'Generator',
      'watermaker': 'Watermaker',
      'sewage_treatment': 'Sewage Treatment Plant',
      'oily_water_separator': 'Oily Water Separator',
      'air_compressor': 'Air Compressor',
      'autopilot': 'Autopilot',
      'ecdis': 'ECDIS',
      'wind_instruments': 'Wind Instruments',
      'speed_log': 'Speed Log',
      'weather_fax': 'Weather Fax Receiver',
      'satellite_comms': 'Satellite Communications'
    };
    return names[equipment] || equipment.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="space-y-6">
      {/* System Configuration */}
      <div>
        <h3 className="text-base font-medium text-gray-900">System Configuration</h3>
        <p className="mt-1 text-sm text-gray-600">Configure vessel systems and equipment</p>
      </div>

      {/* Mandatory Equipment Notice */}
      {mandatoryEquipment.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-900">Mandatory Navigation & Communication Equipment</h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>Based on your vessel category and specifications, the following equipment is required:</p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  {mandatoryEquipment.includes('gps') && <li>GPS Navigation System</li>}
                  {mandatoryEquipment.includes('echo_sounder') && <li>Echo Sounder</li>}
                  {mandatoryEquipment.includes('radar') && <li>Radar</li>}
                  {mandatoryEquipment.includes('ais') && <li>AIS (Automatic Identification System)</li>}
                  {mandatoryEquipment.includes('navtex') && <li>NAVTEX Receiver</li>}
                  {mandatoryEquipment.includes('mf_radio') && <li>MF Radio</li>}
                  {mandatoryEquipment.includes('epirb') && <li>EPIRB</li>}
                  {mandatoryEquipment.includes('sart') && <li>SART</li>}
                  {mandatoryEquipment.includes('pa_system') && <li>Public Address System</li>}
                  {mandatoryEquipment.includes('general_alarm') && <li>General Alarm System</li>}
                  {mandatoryEquipment.includes('fixed_fire_system') && <li>Fixed Fire Suppression System</li>}
                  {mandatoryEquipment.includes('hvac') && <li>HVAC System</li>}
                  {mandatoryEquipment.includes('galley_equipment') && <li>Galley Equipment</li>}
                  {mandatoryEquipment.includes('watermaker') && <li>Watermaker</li>}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Vessel Type-Specific Equipment Notice */}
      {vesselTypeEquipment.length > 0 && vesselConfig.vesselType && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-amber-900">
                {getVesselTypeDisplayName(vesselConfig.vesselType)} Vessel-Specific Requirements
              </h3>
              <div className="mt-2 text-sm text-amber-700">
                <p>The following equipment is required for {getVesselTypeDisplayName(vesselConfig.vesselType)} vessels:</p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  {vesselTypeEquipment.map(eq => (
                    <li key={eq}>{getEquipmentDisplayName(eq)}</li>
                  ))}
                </ul>
                {vesselConfig.towing_capable && (
                  <p className="mt-2 font-medium">Note: This vessel is configured for towing operations.</p>
                )}
                {vesselConfig.carriesPassengers && (
                  <p className="mt-2 font-medium">Note: This vessel carries passengers.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation & Communication Equipment */}
      <NavigationEquipment 
        vesselConfig={vesselConfig}
        updateConfig={updateConfig}
        mandatoryEquipment={mandatoryEquipment}
      />

      {/* Safety Equipment */}
      <SafetyEquipment 
        vesselConfig={vesselConfig}
        updateConfig={updateConfig}
      />

      {/* Fixed Fire Suppression Systems - WBC3 Space-Specific */}
      <div>
        <h3 className="text-base font-medium text-gray-900 mb-4">Fixed Fire Suppression Systems</h3>
        <p className="text-sm text-gray-600 mb-4">WBC3 requires space-specific fixed systems (not vessel-wide)</p>
        <div className="space-y-3">
          {/* Engine Space - Auto-lock when mandatory */}
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Engine Compartment Fixed System</p>
              {vesselConfig.enginePosition === 'below_deck' && vesselConfig.engineType === 'diesel' ? (
                <p className="text-sm text-blue-600 font-medium">MANDATORY - WBC3 Table 16.1.1.1 (inboard diesel below deck)</p>
              ) : (
                <p className="text-sm text-gray-600">Optional - Fixed fire suppression in engine spaces</p>
              )}
            </div>
            <Switch
              checked={vesselConfig.fixedFFE?.engine || 
                      (vesselConfig.enginePosition === 'below_deck' && vesselConfig.engineType === 'diesel') || 
                      false}
              onChange={(checked) => updateConfig({ 
                fixedFFE: {
                  ...vesselConfig.fixedFFE,
                  engine: checked
                }
              })}
              disabled={vesselConfig.enginePosition === 'below_deck' && vesselConfig.engineType === 'diesel'}
            />
          </div>

          {/* Galley Space - Optional */}
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Galley Fixed System</p>
              <p className="text-sm text-gray-600">
                Optional - Alternative to portable protection (WBC3 16.4)
                {vesselConfig.openFlameProtection?.fixedSystem && (
                  <span className="block text-orange-600 text-xs mt-1">
                    ✓ Selected for open-flame protection
                  </span>
                )}
              </p>
            </div>
            <Switch
              checked={vesselConfig.fixedFFE?.galley || vesselConfig.openFlameProtection?.fixedSystem || false}
              onChange={(checked) => {
                // Keep both flags in sync - openFlameProtection.fixedSystem is source of truth
                updateConfig({ 
                  fixedFFE: {
                    ...vesselConfig.fixedFFE,
                    galley: checked
                  },
                  openFlameProtection: {
                    ...vesselConfig.openFlameProtection,
                    fixedSystem: checked
                  }
                });
              }}
            />
          </div>

          {/* Accommodation Space - Optional */}
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Accommodation Fixed System</p>
              <p className="text-sm text-gray-600">Optional - Alternative to portable extinguishers (WBC3 16.2.3.1)</p>
            </div>
            <Switch
              checked={vesselConfig.fixedFFE?.accommodation || false}
              onChange={(checked) => updateConfig({ 
                fixedFFE: {
                  ...vesselConfig.fixedFFE,
                  accommodation: checked
                }
              })}
            />
          </div>
        </div>

        {/* Info box about legacy systems */}
        {vesselConfig.hasFixedFireSystem && !vesselConfig.fixedFFE && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              <span className="font-medium">Migration Notice:</span> This vessel has a legacy fixed system flag. 
              Please specify which spaces have fixed systems above.
            </p>
          </div>
        )}
      </div>

      {/* Vessel Systems */}
      <VesselSystems 
        vesselConfig={vesselConfig}
        updateConfig={updateConfig}
      />

      {/* Engineering Systems */}
      <div>
        <h3 className="text-base font-medium text-gray-900 mb-4">Engineering Systems</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Emergency Steering</p>
              <p className="text-sm text-gray-600">Vessel has emergency steering arrangements</p>
            </div>
            <Switch
              checked={vesselConfig.hasEmergencySteering || vesselConfig.emergency_steering}
              onChange={(checked) => updateConfig({ hasEmergencySteering: checked, emergency_steering: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Automatic Bilge System</p>
              <p className="text-sm text-gray-600">Bilge pumps operate automatically without crew intervention</p>
            </div>
            <Switch
              checked={vesselConfig.bilge_auto}
              onChange={(checked) => updateConfig({ bilge_auto: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Emergency Generator</p>
              <p className="text-sm text-gray-600">Backup power generator fitted</p>
            </div>
            <Switch
              checked={vesselConfig.emergencyGenerator}
              onChange={(checked) => updateConfig({ emergencyGenerator: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Dedicated Engineer Onboard</p>
              <p className="text-sm text-gray-600">Vessel operates with dedicated engineering personnel</p>
            </div>
            <Switch
              checked={vesselConfig.engineer_onboard}
              onChange={(checked) => updateConfig({ engineer_onboard: checked })}
            />
          </div>
        </div>
      </div>

      {/* Electrical Systems */}
      <div>
        <h3 className="text-base font-medium text-gray-900 mb-4">Electrical Systems</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Electrical Systems Installed</p>
              <p className="text-sm text-gray-600">Vessel has electrical systems beyond basic navigation lights</p>
            </div>
            <Switch
              checked={vesselConfig.hasElectricalSystem}
              onChange={(checked) => updateConfig({ hasElectricalSystem: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Centralised Electrical System</p>
              <p className="text-sm text-gray-600">Centralized electrical system requiring emergency backup per WBC3 9.7.1.1</p>
            </div>
            <Switch
              checked={vesselConfig.hasCentralElectrical || vesselConfig.power_critical_systems}
              onChange={(checked) => updateConfig({ hasCentralElectrical: checked, power_critical_systems: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Fixed Radio Equipment</p>
              <p className="text-sm text-gray-600">Fixed radio equipment requiring emergency power per WBC3 9.7.2.1</p>
            </div>
            <Switch
              checked={vesselConfig.hasFixedRadio}
              onChange={(checked) => updateConfig({ hasFixedRadio: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Electrical Navigation Equipment</p>
              <p className="text-sm text-gray-600">Electrically powered navigation equipment per WBC3 9.7.3.1</p>
            </div>
            <Switch
              checked={vesselConfig.hasElectricalNavEquipment}
              onChange={(checked) => updateConfig({ hasElectricalNavEquipment: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Backup Battery Systems</p>
              <p className="text-sm text-gray-600">Backup battery systems per WBC3 9.3.1.5</p>
            </div>
            <Switch
              checked={vesselConfig.hasBackupBatteries}
              onChange={(checked) => updateConfig({ hasBackupBatteries: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Ventilated Battery Types</p>
              <p className="text-sm text-gray-600">Ventilated battery types requiring drip trays per WBC3 9.3.2.2</p>
            </div>
            <Switch
              checked={vesselConfig.hasVentilatedBatteries}
              onChange={(checked) => updateConfig({ hasVentilatedBatteries: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Battery Charging System</p>
              <p className="text-sm text-gray-600">Battery charging system requiring overcharge protection per WBC3 9.3.1.3</p>
            </div>
            <Switch
              checked={vesselConfig.hasBatteryCharger}
              onChange={(checked) => updateConfig({ hasBatteryCharger: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Explosion Risk Spaces</p>
              <p className="text-sm text-gray-600">Spaces with explosion risk requiring ATEX equipment per WBC3 9.5.1</p>
            </div>
            <Switch
              checked={vesselConfig.hasHazardousSpaces}
              onChange={(checked) => updateConfig({ hasHazardousSpaces: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">High Voltage Systems</p>
              <p className="text-sm text-gray-600">High voltage systems (&gt;1000V AC / &gt;1500V DC) per WBC3 definition</p>
            </div>
            <Switch
              checked={vesselConfig.hasHighVoltage}
              onChange={(checked) => updateConfig({ hasHighVoltage: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Night Deck Operations</p>
              <p className="text-sm text-gray-600">Conducts deck operations at night requiring work area lighting per WBC3 9.2.5</p>
            </div>
            <Switch
              checked={vesselConfig.hasNightDeckOps}
              onChange={(checked) => updateConfig({ hasNightDeckOps: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Lightning Risk Operations</p>
              <p className="text-sm text-gray-600">Operates in areas with considerable lightning strike risk per WBC3 9.6.1</p>
            </div>
            <Switch
              checked={vesselConfig.hasLightningRisk}
              onChange={(checked) => updateConfig({ hasLightningRisk: checked })}
            />
          </div>
        </div>
      </div>

      {/* Fire Safety & Accommodation */}
      <div>
        <h3 className="text-base font-medium text-gray-900 mb-4">Fire Safety & Accommodation</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Accommodation Spaces</p>
              <p className="text-sm text-gray-600">Vessel has accommodation spaces requiring fire protection per WBC3 16.2.1.2</p>
            </div>
            <Switch
              checked={vesselConfig.hasAccommodation}
              onChange={(checked) => updateConfig({ hasAccommodation: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Sleeping Accommodation</p>
              <p className="text-sm text-gray-600">Vessel has sleeping accommodation requiring fire protection</p>
            </div>
            <Switch
              checked={vesselConfig.hasSleeping}
              onChange={(checked) => updateConfig({ hasSleeping: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Cooking Facilities</p>
              <p className="text-sm text-gray-600">Vessel has cooking facilities per WBC3 Table 16.2.3.1</p>
            </div>
            <Switch
              checked={vesselConfig.hasCooking}
              onChange={(checked) => updateConfig({ hasCooking: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Heating Systems</p>
              <p className="text-sm text-gray-600">Vessel has heating systems requiring fire protection</p>
            </div>
            <Switch
              checked={vesselConfig.hasHeating}
              onChange={(checked) => updateConfig({ hasHeating: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Open Flame Appliances</p>
              <p className="text-sm text-gray-600">Vessel has open flame appliances per WBC3 15.6.2.1</p>
            </div>
            <Switch
              checked={vesselConfig.hasOpenFlame}
              onChange={(checked) => updateConfig({ hasOpenFlame: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Gas Appliances</p>
              <p className="text-sm text-gray-600">Vessel has gas consuming appliances requiring gas detection</p>
            </div>
            <Switch
              checked={vesselConfig.hasGasAppliance}
              onChange={(checked) => updateConfig({ hasGasAppliance: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Liquid Fuel Appliances</p>
              <p className="text-sm text-gray-600">Vessel has liquid fuel appliances requiring fire detection</p>
            </div>
            <Switch
              checked={vesselConfig.hasLiquidFuelAppliance}
              onChange={(checked) => updateConfig({ hasLiquidFuelAppliance: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Open Boat Type</p>
              <p className="text-sm text-gray-600">Vessel is open boat type per WBC3 Table 16.1.1.1 Note d</p>
            </div>
            <Switch
              checked={vesselConfig.isOpenBoat}
              onChange={(checked) => updateConfig({ isOpenBoat: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Substantial Enclosure</p>
              <p className="text-sm text-gray-600">Vessel has substantial enclosure (affects open boat classification)</p>
            </div>
            <Switch
              checked={vesselConfig.hasSubstantialEnclosure}
              onChange={(checked) => updateConfig({ hasSubstantialEnclosure: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Exhaust Leak Risk Areas</p>
              <p className="text-sm text-gray-600">Spaces where exhaust gases may accumulate per WBC3 15.6.3.3</p>
            </div>
            <Switch
              checked={vesselConfig.hasExhaustLeakRisk}
              onChange={(checked) => updateConfig({ hasExhaustLeakRisk: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Designated Escape Routes</p>
              <p className="text-sm text-gray-600">Vessel has designated escape routes per WBC3 15.7.4</p>
            </div>
            <Switch
              checked={vesselConfig.hasEscapeRoute}
              onChange={(checked) => updateConfig({ hasEscapeRoute: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Fire Risk Areas</p>
              <p className="text-sm text-gray-600">Areas identified to increase the risk of fire per WBC3 16.5.2</p>
            </div>
            <Switch
              checked={vesselConfig.hasFireRiskArea}
              onChange={(checked) => updateConfig({ hasFireRiskArea: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Fire Ports</p>
              <p className="text-sm text-gray-600">Vessel has fire ports for fire fighting access</p>
            </div>
            <Switch
              checked={vesselConfig.hasFirePort}
              onChange={(checked) => updateConfig({ hasFirePort: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Gas Leak Risk</p>
              <p className="text-sm text-gray-600">Spaces into which flammable gas may leak per WBC3 15.6.4.1</p>
            </div>
            <Switch
              checked={vesselConfig.hasGasLeakRisk}
              onChange={(checked) => updateConfig({ hasGasLeakRisk: checked })}
            />
          </div>
        </div>
      </div>

      {/* Dangerous Goods & Special Operations */}
      <div>
        <h3 className="text-base font-medium text-gray-900 mb-4">Dangerous Goods & Special Operations</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Carries Dangerous Goods</p>
              <p className="text-sm text-gray-600">Vessel carries dangerous goods requiring additional fire protection per WBC3 29</p>
            </div>
            <Switch
              checked={vesselConfig.hasDangerousGoods}
              onChange={(checked) => updateConfig({ hasDangerousGoods: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">DG Near Accommodation/Fuel/Machinery</p>
              <p className="text-sm text-gray-600">Dangerous goods within 3m of accommodation/fuel/machinery per WBC3 29.7.1</p>
            </div>
            <Switch
              checked={vesselConfig.dgNearAccommodation}
              onChange={(checked) => updateConfig({ dgNearAccommodation: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Choose Fixed Fire System</p>
              <p className="text-sm text-gray-600">Operator chooses fixed fire system over portable (for above deck engines)</p>
            </div>
            <Switch
              checked={vesselConfig.choosesFixed}
              onChange={(checked) => updateConfig({ choosesFixed: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Transitioning from Old Codes</p>
              <p className="text-sm text-gray-600">Existing vessel transitioning from old codes per WBC3 transition provisions</p>
            </div>
            <Switch
              checked={vesselConfig.isTransitioning}
              onChange={(checked) => updateConfig({ isTransitioning: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Fire Buckets Practicable</p>
              <p className="text-sm text-gray-600">Fire buckets are practicable per WBC3 16.6.1</p>
            </div>
            <Switch
              checked={vesselConfig.isPracticable}
              onChange={(checked) => updateConfig({ isPracticable: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Fire Buckets Deemed Necessary</p>
              <p className="text-sm text-gray-600">Fire buckets deemed necessary by Certifying Authority per WBC3 16.6.1</p>
            </div>
            <Switch
              checked={vesselConfig.deemedNecessary}
              onChange={(checked) => updateConfig({ deemedNecessary: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Company Requires Fire Buckets</p>
              <p className="text-sm text-gray-600">Company SMS requires fire buckets beyond regulatory minimum</p>
            </div>
            <Switch
              checked={vesselConfig.companyRequiresBuckets}
              onChange={(checked) => updateConfig({ companyRequiresBuckets: checked })}
            />
          </div>
        </div>
      </div>

      {/* Equipment Check Schedule */}
      <div>
        <h3 className="text-base font-medium text-gray-900 mb-4">Equipment Check Schedule</h3>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-blue-900">
            <strong>Legal Requirement:</strong> Annual inspection only
          </p>
          <p className="text-sm text-blue-700 mt-2">
            Many operators choose more frequent checks for operational safety. Select your preferred frequency:
          </p>
        </div>
        
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="lsa-frequency" className="block text-sm font-medium text-gray-900">Life-Saving Appliances (LSA)</label>
            <Select
              id="lsa-frequency"
              value={vesselConfig.lsaCheckFrequency || 'monthly'}
              onChange={(e) => updateConfig({ lsaCheckFrequency: e.target.value })}
              className="mt-1"
            >
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly (Recommended)</option>
              <option value="quarterly">Quarterly</option>
              <option value="annually">Annually (Legal minimum)</option>
            </Select>
            <p className="mt-1 text-sm text-gray-600">Lifejackets, lifebuoys, flares, etc.</p>
          </div>

          <div>
            <label htmlFor="ffe-frequency" className="block text-sm font-medium text-gray-900">Fire-Fighting Equipment (FFE)</label>
            <Select
              id="ffe-frequency"
              value={vesselConfig.ffeCheckFrequency || 'monthly'}
              onChange={(e) => updateConfig({ ffeCheckFrequency: e.target.value })}
              className="mt-1"
            >
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly (Recommended)</option>
              <option value="quarterly">Quarterly</option>
              <option value="annually">Annually (Legal minimum)</option>
            </Select>
            <p className="mt-1 text-sm text-gray-600">Extinguishers, hoses, detectors, etc.</p>
          </div>
        </div>
        
        <p className="mt-4 text-xs text-gray-500">
          Note: You can also set custom intervals for individual equipment items in the maintenance schedule.
        </p>
      </div>
    </div>
  );
};

export default SystemsTab;